import { useEffect, useState } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { createDonationPaymentIntent } from "../../components/api/api";
import Button from "components/Button/Button";
import "./donate.scss";

const PRESET_AMOUNTS = [10, 25, 50];
const MIN_AMOUNT_CENTS = 100;
const MAX_AMOUNT_CENTS = 1000000;

const DonationPaymentForm = ({
  amount,
  onPaymentSuccess,
  onPaymentError,
  isSubmitting,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    if (stripe && elements && isSubmitting) {
      const handlePayment = async () => {
        try {
          const result = await stripe.confirmPayment({
            elements,
            redirect: "if_required",
          });
          if (result.error) {
            setPaymentError(result.error.message);
            onPaymentError(result.error.message);
          } else if (
            result.paymentIntent &&
            result.paymentIntent.status === "succeeded"
          ) {
            setPaymentError("");
            onPaymentSuccess(result.paymentIntent);
          }
        } catch (error) {
          setPaymentError("Payment failed. Please try again.");
          onPaymentError("Payment failed. Please try again.");
        }
      };
      handlePayment();
    }
  }, [stripe, elements, isSubmitting, onPaymentSuccess, onPaymentError]);

  return (
    <div
      className={`donate-stripe-container ${
        paymentError ? "payment-error" : ""
      }`}
    >
      <p className="donate-amount-label">
        Donation amount: ${amount.toFixed(2)} CAD
      </p>
      <PaymentElement />
      {paymentError && (
        <div className="donate-payment-error">
          <span className="error-icon">!</span>
          <span>{paymentError}</span>
        </div>
      )}
    </div>
  );
};

function Donate() {
  const [stripePromise, setStripePromise] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/config`)
      .then((res) => res.json())
      .then((config) => {
        setStripePromise(loadStripe(config.publishableKey));
      })
      .catch(() => setError("Failed to load payment"));
  }, []);

  const getAmountCents = () => {
    if (customAmount) {
      const parsed = parseFloat(customAmount);
      if (isNaN(parsed)) return null;
      return Math.round(parsed * 100);
    }
    return selectedAmount ? selectedAmount * 100 : null;
  };

  const amountCents = getAmountCents();
  const isValidAmount =
    amountCents !== null &&
    amountCents >= MIN_AMOUNT_CENTS &&
    amountCents <= MAX_AMOUNT_CENTS;

  const handleAmountSelect = (amount) => {
    setCustomAmount("");
    setSelectedAmount(amount);
    setClientSecret("");
    setError("");
  };

  const handleCustomAmountChange = (e) => {
    setSelectedAmount(null);
    setCustomAmount(e.target.value);
    setClientSecret("");
    setError("");
  };

  const handleProceedToPayment = async () => {
    if (!isValidAmount) {
      setError("Please select an amount between $1 and $10,000");
      return;
    }
    setError("");
    const secret = await createDonationPaymentIntent(amountCents);
    if (secret) {
      setClientSecret(secret);
    } else {
      setError("Failed to initialize payment. Please try again.");
    }
  };

  const handleBack = () => {
    setClientSecret("");
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clientSecret) return;
    setIsSubmitting(true);
    setError("");
  };

  const handlePaymentSuccess = () => {
    setShowConfirmation(true);
    setIsSubmitting(false);
  };

  const handlePaymentError = (msg) => {
    setError(msg);
    setIsSubmitting(false);
  };

  if (showConfirmation) {
    return (
      <div className="donate-page">
        <div className="donate-content">
          <div className="donate-confirmation">
            <div className="donate-confirmation-icon">✓</div>
            <h1 className="donate-confirmation-title">Thank you!</h1>
            <p className="donate-confirmation-message">
              Your donation has been received. We appreciate your support!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="donate-page">
      <div className="donate-content">
        <div className="donate-hero">
          <h1 className="donate-title">Support Our Work</h1>
          <p className="donate-description">
            Your donation helps support our projects and initiatives. Every
            contribution makes a difference—thank you for considering a gift!
          </p>
        </div>

        {!clientSecret ? (
          <div className="donate-amount-section">
            <h2 className="donate-section-title">Choose an amount</h2>
            <div className="donate-preset-amounts">
              {PRESET_AMOUNTS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`donate-amount-btn ${
                    selectedAmount === amount ? "selected" : ""
                  }`}
                  onClick={() => handleAmountSelect(amount)}
                >
                  ${amount}
                </button>
              ))}
            </div>
            <div className="donate-custom-amount">
              <label htmlFor="custom-amount">
                Or enter a custom amount ($)
              </label>
              <input
                id="custom-amount"
                type="number"
                min="1"
                max="10000"
                step="0.01"
                placeholder="e.g. 15"
                value={customAmount}
                onChange={handleCustomAmountChange}
                className="donate-custom-input"
              />
            </div>
            {error && <div className="donate-error">{error}</div>}
            <Button
              variant="secondary"
              className="donate-proceed-btn"
              onClick={handleProceedToPayment}
              disabled={!isValidAmount}
            >
              Continue to payment
            </Button>
          </div>
        ) : (
          <form className="donate-payment-form" onSubmit={handleSubmit}>
            <div className="donate-payment-header">
              <button
                type="button"
                className="donate-back-btn"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                ← Change amount
              </button>
            </div>
            {stripePromise && clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: { theme: "stripe" },
                }}
              >
                <DonationPaymentForm
                  amount={amountCents / 100}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  isSubmitting={isSubmitting}
                />
              </Elements>
            )}
            {error && <div className="donate-error">{error}</div>}
            <Button
              onClick={handleSubmit}
              type="submit"
              variant="secondary"
              className="donate-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Processing..."
                : `Donate $${(amountCents / 100).toFixed(2)}`}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Donate;
